'use client';

import { useCallback, type ReactNode } from 'react';
import {
  ChatFindingSearchProvider,
  formatCategoryForDisplay,
  toSearchHarEntryResult,
  type SearchAssetResult,
  type SearchHarEntryResult,
  type SearchSignalResult,
} from '@terra/ui-design-system';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { terraProxyApiClient } from '../../../clients/testerClient';
import { listTargetSignalsForScope } from '../../../hooks/api/useTargetSignals';
import { useApplicationById } from '../../../hooks/api/useApplicationById';
import { isAppTarget } from '../../../utils/target-scope.utils';

type ChatFindingSearchWrapperProps = {
  targetId: string | undefined;
  targetType: TargetTypeType;
  children: ReactNode;
};

function toSearchSignalResult(signal: GroupedSignal): SearchSignalResult {
  return {
    id: signal.id,
    label:
      signal.description ||
      formatCategoryForDisplay(signal.category) ||
      `Signal ${signal.id.slice(0, 8)}`,
    category: signal.category ?? undefined,
    status: signal.status,
    description: signal.description ?? undefined,
    createdAt: signal.createdAt
      ? new Date(signal.createdAt).toISOString()
      : undefined,
    endpointId: signal.endpointId ?? undefined,
    harEntryId: signal.harEntryId ?? undefined,
    entryPointId: signal.entryPointId ?? undefined,
    url: signal.endpoint?.url ?? undefined,
    method: signal.endpoint?.request?.method ?? undefined,
    isAppWide: signal.isAppWide === true,
    origin: signal.origin,
  };
}

export function ChatFindingSearchWrapper({
  targetId,
  targetType,
  children,
}: ChatFindingSearchWrapperProps) {
  const applicationId = isAppTarget(targetType) ? targetId : undefined;
  const { application, isLoading: isLoadingApplication } = useApplicationById({
    id: applicationId,
  });
  const defaultCvssVersion =
    applicationId && !isLoadingApplication
      ? application?.cvssVersion === 'V3_1'
        ? '3.1'
        : '4.0'
      : applicationId
        ? undefined
        : '4.0';

  const handleSearchSignals = useCallback(
    async (query: string): Promise<SearchSignalResult[]> => {
      if (!targetId) return [];

      try {
        const signals = await listTargetSignalsForScope({
          targetId,
          targetType,
          limit: 50,
          filters: { description: query || undefined },
        });

        if (signals.length === 0) return [];

        return signals.map(toSearchSignalResult);
      } catch (error) {
        console.error('Failed to search signals:', error);
        return [];
      }
    },
    [targetId, targetType],
  );

  const handleGetSignalById = useCallback(
    async (signalId: string): Promise<SearchSignalResult | null> => {
      try {
        const signal = await terraProxyApiClient.getSignal(signalId);

        return toSearchSignalResult(signal);
      } catch (error) {
        console.error('Failed to get signal by ID:', error);
        return null;
      }
    },
    [],
  );

  const handleSearchAssets = useCallback(
    async (query: string): Promise<SearchAssetResult[]> => {
      if (!applicationId) return [];

      try {
        const endpointsResult = await terraProxyApiClient.getEndpointsPreview(
          applicationId,
          {
            url: query,
            limit: 20,
          },
        );

        return (endpointsResult?.endpoints ?? []).map((endpoint) => ({
          id: endpoint.id,
          url: endpoint.url,
          method: endpoint.request?.method,
          type: 'ENDPOINT' as const,
        }));
      } catch (error) {
        console.error('Failed to search assets:', error);
        return [];
      }
    },
    [applicationId],
  );

  const handleGetEndpointById = useCallback(
    async (endpointId: string): Promise<SearchAssetResult | null> => {
      try {
        const endpoint = await terraProxyApiClient.getEndpointById(endpointId);
        if (!endpoint) return null;
        return {
          id: endpoint.id,
          url: endpoint.url ?? endpoint.path ?? endpointId,
          method: endpoint.request?.method ?? undefined,
          type: 'ENDPOINT',
        };
      } catch (error) {
        console.error('Failed to get endpoint by ID:', error);
        return null;
      }
    },
    [],
  );

  const handleGetHarEntriesByEndpoint = useCallback(
    async (endpointId: string): Promise<SearchHarEntryResult[]> => {
      try {
        const response =
          await terraProxyApiClient.getHarEntriesByEndpoint(endpointId);
        if (response.status !== 200) {
          return [];
        }
        return response.body.harEntries.map(toSearchHarEntryResult);
      } catch (error) {
        console.error('Failed to get HAR entries by endpoint:', error);
        return [];
      }
    },
    [],
  );

  return (
    <ChatFindingSearchProvider
      onSearchSignals={handleSearchSignals}
      onGetSignalById={handleGetSignalById}
      onSearchAssets={handleSearchAssets}
      onGetEndpointById={handleGetEndpointById}
      onGetHarEntriesByEndpoint={handleGetHarEntriesByEndpoint}
      defaultCvssVersion={defaultCvssVersion}
      cvssEnvironmentalRequirements={application?.cvssEnvironmentalRequirements}
    >
      {children}
    </ChatFindingSearchProvider>
  );
}
